import React, { useState, useEffect, useCallback } from 'react';
import apiWithAuth from '../../services/apiWithAuth';
import { usePermissions } from '../../hooks/usePermissions';

// --- SVG Icon Components ---
const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
    </svg>
);
const EditIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L15.232 5.232z" />
    </svg>
);
const DeleteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);
const ViewIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
);
const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);
const WarningIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
);

// --- Constants and Auth Token ---
const PLAN_TYPES = ["F_MONTHLY", "B_MONTHLY", "S_MONTHLY", "G_MONTHLY", "F_YEARLY", "B_YEARLY", "S_YEARLY", "G_YEARLY"];
const PLAN_TYPE_LABELS = { F_MONTHLY: 'FREE MONTHLY', B_MONTHLY: 'BRONZE MONTHLY', S_MONTHLY: 'SILVER MONTHLY', G_MONTHLY: 'GOLD MONTHLY', F_YEARLY: 'FREE YEARLY', B_YEARLY: 'BRONZE YEARLY', S_YEARLY: 'SILVER YEARLY', G_YEARLY: 'GOLD YEARLY' };

const resolvePlanDailyRate = (plan) => {
    if (!plan) return 0;
    if (plan.dailyDeductionRate != null && plan.dailyDeductionRate >= 0) {
        return Number(plan.dailyDeductionRate);
    }
    const days = plan.period === 'yearly' ? 365 : 30;
    return parseFloat((plan.price / days).toFixed(2));
};

const PLAN_FEATURE_FLAGS = [
    { key: 'enableCustomerListing', label: 'Customer Listing' },
    { key: 'enableOrderBooking', label: 'Pickup' },
    { key: 'enableDeliveryOption', label: 'Delivery Option' },
    { key: 'enableExchangeRefund', label: 'Exchange Refund' },
    { key: 'enableJobListing', label: 'Job Listing' },
    { key: 'enableShopAnalytics', label: 'Shop Analytics' },
];

// --- PlanForm Modal Component ---
function PlanForm({ form, formErrors, onChange, onSubmit, onCancel, editingId, submitting }) {
    return (
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <header className="flex items-center justify-between p-4 border-b">
                <h2 className="text-xl font-semibold text-gray-800">{editingId ? "Edit Subscription Plan" : "Create New Plan"}</h2>
                <button onClick={onCancel} className="p-1 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600"><CloseIcon /></button>
            </header>
            <form onSubmit={onSubmit} className="flex-1 flex flex-col overflow-hidden">
                <div className="p-6 overflow-y-auto space-y-6">
                    {Object.keys(formErrors).length > 0 && <div className="bg-red-100 text-red-700 p-3 rounded-md text-sm">Please fill out all required fields.</div>}
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Plan Type <span className="text-red-500">*</span></label>
                            <select name="planType" value={form.planType} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required>
                                <option value="">Select plan type</option>
                                {PLAN_TYPES.map(type => <option key={type} value={type}>{PLAN_TYPE_LABELS[type]}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Billing Period <span className="text-red-500">*</span></label>
                            <select name="period" value={form.period} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required>
                                <option value="">Select period</option>
                                {["monthly", "yearly"].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                            </select>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹) <span className="text-red-500">*</span></label>
                            <input type="number" name="price" value={form.price} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required min="0" step="0.01" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Daily Deduction Rate (credits/day) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                name="dailyDeductionRate"
                                value={form.dailyDeductionRate}
                                onChange={onChange}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${formErrors.dailyDeductionRate ? 'border-red-500' : 'border-gray-300'}`}
                                required
                                min="0"
                                step="0.01"
                                placeholder="e.g., 29.76"
                            />
                            {formErrors.dailyDeductionRate && (
                                <p className="text-xs text-red-600 mt-1">Daily deduction rate is required.</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">Fixed credits deducted per day for wallets on this plan.</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Max Storage (GB) <span className="text-red-500">*</span></label>
                            <input type="number" name="maxStorageGB" value={form.maxStorageGB} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required min="0" />
                        </div>
                    </div>

                    <hr/>
                    <h3 className="text-md font-semibold text-gray-800">Usage Limits</h3>
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Max Shop Members <span className="text-gray-500 text-xs">(-1 for ∞)</span></label>
                            <input type="number" name="maxShopMembers" value={form.maxShopMembers} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Max Coupons/Month <span className="text-gray-500 text-xs">(-1 for ∞)</span></label>
                            <input type="number" name="maxCouponsPerMonth" value={form.maxCouponsPerMonth} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Max KDP Feeds/Month <span className="text-gray-500 text-xs">(-1 for ∞)</span></label>
                            <input type="number" name="maxKdpFeedsPerMonth" value={form.maxKdpFeedsPerMonth} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                        </div>
                         <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Max Additional Shops</label>
                            <input type="number" name="maxAdditionalShops" value={form.maxAdditionalShops} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                        </div>
                         <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Max Local Bloggers</label>
                            <input type="number" name="maxLocalBloggers" value={form.maxLocalBloggers} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                        </div>
                         <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Max Relationship Managers</label>
                            <input type="number" name="maxRelationshipManagers" value={form.maxRelationshipManagers} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                        </div>
                    </div>
                    
                    <hr/>
                    <h3 className="text-md font-semibold text-gray-800">Feature Flags</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {PLAN_FEATURE_FLAGS.map(({ key, label }) => (
                             <label key={key} className="flex items-center gap-2">
                                <input type="checkbox" name={key} checked={!!form[key]} onChange={onChange} className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                                <span className="font-medium text-gray-700">{label}</span>
                            </label>
                        ))}
                    </div>

                </div>
                <footer className="flex gap-3 justify-end p-4 bg-gray-50 border-t mt-auto">
                    <button type="button" onClick={onCancel} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">Cancel</button>
                    <button type="submit" disabled={submitting} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50">
                        {submitting ? 'Saving...' : (editingId ? 'Update Plan' : 'Create Plan')}
                    </button>
                </footer>
            </form>
        </div>
    );
}

// --- PlanTable Component ---
function PlanTable({ plans, loading, submitting, onEdit, onDelete, onView }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500">
                <thead className="bg-gray-50 text-xs text-gray-700 uppercase">
                    <tr>
                        <th scope="col" className="px-6 py-3">Plan</th>
                        <th scope="col" className="px-6 py-3">Price</th>
                        <th scope="col" className="px-6 py-3">Daily Rate</th>
                        <th scope="col" className="px-6 py-3">Storage</th>
                        <th scope="col" className="px-6 py-3 text-right">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr><td colSpan="5" className="text-center py-12 text-gray-500">Loading plans...</td></tr>
                    ) : plans.length === 0 ? (
                        <tr><td colSpan="5" className="text-center py-12 text-gray-500">No plans found.</td></tr>
                    ) : (
                        plans.map((plan, index) => (
                            <tr key={plan._id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-indigo-50`}>
                                <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
                                    <div className="font-bold">{PLAN_TYPE_LABELS[plan.planType] || plan.planType}</div>
                                    <div className="text-xs text-gray-500 capitalize">{plan.period}</div>
                                </td>
                                <td className="px-6 py-4">₹{plan.price.toLocaleString('en-IN')}</td>
                                <td className="px-6 py-4">
                                    {`${resolvePlanDailyRate(plan).toFixed(2)}/day`}
                                    {plan.dailyDeductionRate == null && (
                                        <span className="block text-xs text-amber-600">not saved yet</span>
                                    )}
                                </td>
                                <td className="px-6 py-4">{plan.maxStorageGB} GB</td>
                                <td className="px-6 py-4 text-right">
                                    <div className="flex items-center justify-end space-x-2">
                                        <button onClick={() => onView(plan)} className="p-2 text-gray-400 hover:text-blue-600 group relative">
                                            <ViewIcon /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-700 text-white text-xs rounded py-1 px-2">View</span>
                                        </button>
                                        <button onClick={() => onEdit(plan)} className="p-2 text-gray-400 hover:text-indigo-600 group relative">
                                            <EditIcon /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-700 text-white text-xs rounded py-1 px-2">Edit</span>
                                        </button>
                                        <button onClick={() => onDelete(plan._id)} className="p-2 text-gray-400 hover:text-red-600 group relative">
                                            <DeleteIcon /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-700 text-white text-xs rounded py-1 px-2">Delete</span>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}

// --- DeleteModal Component ---
function DeleteModal({ open, onCancel, onConfirm, submitting }) {
    if (!open) return null;
    return (
        <div className={`fixed inset-0 bg-black bg-opacity-60 z-40 flex justify-center items-center p-4 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className={`bg-white rounded-lg shadow-2xl w-full max-w-sm transform transition-all duration-300 ${open ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
                <div className="p-6 text-center">
                    <WarningIcon />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Deletion</h3>
                    <p className="text-sm text-gray-600 mb-6">Are you sure you want to delete this plan? This action cannot be undone.</p>
                    <div className="flex justify-center gap-3">
                        <button onClick={onCancel} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">Cancel</button>
                        <button onClick={onConfirm} disabled={submitting} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50">
                            {submitting ? 'Deleting...' : 'Delete'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- PlanViewModal (Reverted to original) ---
function PlanViewModal({ open, plan, onClose }) {
    if (!open || !plan) return null;
    const renderFeatureToggle = (label, isEnabled) => (
        <div className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded-lg transition-colors">
            <span className="font-medium text-gray-700">{label}</span>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${isEnabled
                ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20'
                : 'bg-red-50 text-red-700 ring-1 ring-red-600/20'
                }`}>
                {isEnabled ? (
                    <>
                        <svg className="w-3 h-3 mr-1.5 text-green-500" fill="currentColor" viewBox="0 0 8 8">
                            <circle cx={4} cy={4} r={3} />
                        </svg>
                        Enabled
                    </>
                ) : (
                    <>
                        <svg className="w-3 h-3 mr-1.5 text-red-500" fill="currentColor" viewBox="0 0 8 8">
                            <circle cx={4} cy={4} r={3} />
                        </svg>
                        Disabled
                    </>
                )}
            </span>
        </div>
    );
    return (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white shadow-xl w-full max-w-4xl max-h-[80vh] overflow-y-auto transform transition-all duration-300 ease-out animate-slide-up">
                {/* Modal Header */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Plan Details</h2>
                            <p className="text-gray-500 mt-1">Comprehensive overview of subscription features</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                            aria-label="Close"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>
                </div>
                {/* Modal Content */}
                <div className="p-6 space-y-8">
                    {/* Plan Summary Card */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <div className="space-y-1">
                                <h3 className="text-lg font-semibold text-gray-800">{PLAN_TYPE_LABELS[plan.planType] || plan.planType.replace('_', ' ')}</h3>
                                <p className="text-gray-500 capitalize">{plan.period} billing</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-gray-500">Price</p>
                                <p className="text-3xl font-bold text-blue-600">₹{plan.price.toLocaleString('en-IN')}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-gray-500">Daily Deduction Rate</p>
                                <p className="text-xl font-semibold text-gray-800">
                                    {`${resolvePlanDailyRate(plan).toFixed(2)} credits/day`}
                                    {plan.dailyDeductionRate == null && (
                                        <span className="block text-xs text-amber-600 font-normal">Using fallback until saved on server</span>
                                    )}
                                </p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-gray-500">Storage</p>
                                <p className="text-xl font-semibold text-gray-800">{plan.maxStorageGB} GB</p>
                            </div>
                        </div>
                    </div>
                    {/* Details Sections */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Core Features */}
                        <div className="space-y-6">
                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
                                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Core Features
                                </h3>
                                <div className="space-y-4">
                                    <div className="flex justify-between py-2 border-b border-gray-100">
                                        <span className="text-gray-600">Shop Members</span>
                                        <span className="font-medium text-gray-900">
                                            {plan.maxShopMembers === -1 ? 'Unlimited' : plan.maxShopMembers}
                                        </span>
                                    </div>
                                    <div className="flex justify-between py-2 border-b border-gray-100">
                                        <span className="text-gray-600">Additional Shops</span>
                                        <span className="font-medium text-gray-900">{plan.maxAdditionalShops}</span>
                                    </div>
                                    <div className="flex justify-between py-2 border-b border-gray-100">
                                        <span className="text-gray-600">Local Bloggers</span>
                                        <span className="font-medium text-gray-900">{plan.maxLocalBloggers}</span>
                                    </div>
                                    <div className="flex justify-between py-2">
                                        <span className="text-gray-600">Relationship Managers</span>
                                        <span className="font-medium text-gray-900">{plan.maxRelationshipManagers}</span>
                                    </div>
                                </div>
                            </div>
                            {/* Feature Toggles */}
                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
                                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    Feature Flags
                                </h3>
                                <div className="grid grid-cols-1 gap-2">
                                    {renderFeatureToggle("Customer Listing", plan.enableCustomerListing)}
                                    {renderFeatureToggle("Pickup", plan.enableOrderBooking)}
                                    {renderFeatureToggle("Delivery Option", plan.enableDeliveryOption)}
                                    {renderFeatureToggle("Exchange/Refund", plan.enableExchangeRefund)}
                                    {renderFeatureToggle("Job Listing", plan.enableJobListing)}
                                    {renderFeatureToggle("Shop Analytics", plan.enableShopAnalytics)}
                                </div>
                            </div>
                        </div>
                        {/* Usage Limits */}
                        <div className="space-y-6">
                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
                                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                    Usage Limits
                                </h3>
                                <div className="space-y-4">
                                    <div className="flex justify-between py-2 border-b border-gray-100">
                                        <span className="text-gray-600">Coupons per Month</span>
                                        <span className="font-medium text-gray-900">
                                            {plan.maxCouponsPerMonth === -1 ? 'Unlimited' : plan.maxCouponsPerMonth}
                                        </span>
                                    </div>
                                    <div className="flex justify-between py-2 border-b border-gray-100">
                                        <span className="text-gray-600">KDP Feeds per Month</span>
                                        <span className="font-medium text-gray-900">
                                            {plan.maxKdpFeedsPerMonth === -1 ? 'Unlimited' : plan.maxKdpFeedsPerMonth}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {/* Restrictions */}
                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
                                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                    Restrictions & Details
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500 mb-1">Restricted Features</h4>
                                        <p className="text-gray-700 bg-gray-50 p-3 rounded-lg text-sm">
                                            {plan.restrictedFeatures && plan.restrictedFeatures.length > 0
                                                ? plan.restrictedFeatures.join(", ")
                                                : "No restricted features"}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500 mb-1">Additional Features</h4>
                                        {plan.features && plan.features.length > 0 ? (
                                            <ul className="space-y-2">
                                                {plan.features.map((f, i) => (
                                                    <li key={i} className="flex items-start">
                                                        <svg className="flex-shrink-0 w-4 h-4 text-blue-500 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                        <span className="text-gray-700">{f}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-gray-500 italic">No additional features listed</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Modal Footer */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    >
                        Close Details
                    </button>
                </div>
            </div>
        </div>
    );
}


// --- Main SubscriptionPlanManager Component ---
function getDefaultForm() {
    return {
        planType: "", period: "", price: "", dailyDeductionRate: "", maxStorageGB: "",
        maxShopMembers: 0, enableCustomerListing: false, maxCouponsPerMonth: 0,
        enableOrderBooking: false, enableDeliveryOption: false, enableExchangeRefund: false,
        enableJobListing: false, enableShopAnalytics: false,
        maxKdpFeedsPerMonth: 0, maxAdditionalShops: 0,
        maxLocalBloggers: 0, maxRelationshipManagers: 0, restrictedFeatures: []
    };
}

/** Normalized payload for create/update; always includes shop feature flags for the API. */
function buildSubscriptionPlanPayload(form) {
    const n = (v) => Number(v);
    const b = (v) => Boolean(v);
    return {
        planType: form.planType,
        period: form.period,
        price: n(form.price),
        dailyDeductionRate: parseFloat(Number(form.dailyDeductionRate).toFixed(2)),
        maxStorageGB: n(form.maxStorageGB),
        maxShopMembers: n(form.maxShopMembers),
        maxCouponsPerMonth: n(form.maxCouponsPerMonth),
        maxKdpFeedsPerMonth: n(form.maxKdpFeedsPerMonth),
        maxAdditionalShops: n(form.maxAdditionalShops),
        maxLocalBloggers: n(form.maxLocalBloggers),
        maxRelationshipManagers: n(form.maxRelationshipManagers),
        enableCustomerListing: b(form.enableCustomerListing),
        enableOrderBooking: b(form.enableOrderBooking),
        enableDeliveryOption: b(form.enableDeliveryOption),
        enableExchangeRefund: b(form.enableExchangeRefund),
        enableJobListing: b(form.enableJobListing),
        enableShopAnalytics: b(form.enableShopAnalytics),
        enableShopSettings: b(form.enableShopSettings),
        restrictedFeatures: Array.isArray(form.restrictedFeatures) ? form.restrictedFeatures : [],
    };
}

export default function SubscriptionPlanManager() {
    const permissions = usePermissions('Subscription Plans');
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [form, setForm] = useState(getDefaultForm());
    const [editingId, setEditingId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [viewPlan, setViewPlan] = useState(null);
    const [message, setMessage] = useState("");

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await apiWithAuth.get('/subscription/plans');
            setPlans(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Error fetching plans");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === "checkbox" ? checked : value;
        setForm(f => ({ ...f, [name]: val }));
    };

    const validateForm = () => {
        const errors = {};
        if (!form.planType) errors.planType = "Required";
        if (!form.period) errors.period = "Required";
        if (form.price === "" || isNaN(form.price)) errors.price = "Required";
        if (form.dailyDeductionRate === "" || isNaN(form.dailyDeductionRate) || Number(form.dailyDeductionRate) < 0) {
            errors.dailyDeductionRate = "Required";
        }
        if (form.maxStorageGB === "" || isNaN(form.maxStorageGB)) errors.maxStorageGB = "Required";
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setSubmitting(true);
        setError("");
        try {
            const payload = buildSubscriptionPlanPayload(form);
            const shopFeatureQuery = {
                enableShopAnalytics: payload.enableShopAnalytics,
            };

            if (editingId) {
                await apiWithAuth.put(`/subscription/admin/plan/${editingId}`, payload, { params: shopFeatureQuery });
                setMessage("Plan updated successfully!");
            } else {
                await apiWithAuth.post('/subscription/admin/plan', payload, { params: shopFeatureQuery });
                setMessage("Plan created successfully!");
            }
            await fetchPlans();
            setShowFormModal(false);
        } catch (err) {
            const apiErrors = err.response?.data?.errors;
            const firstApiError = Array.isArray(apiErrors) && apiErrors.length
                ? apiErrors.map((e) => e.msg || e.message).join(', ')
                : null;
            setError(firstApiError || err.response?.data?.message || err.message || "Error submitting form");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (plan) => {
        setForm({
            ...getDefaultForm(),
            ...plan,
            dailyDeductionRate: plan.dailyDeductionRate != null ? String(plan.dailyDeductionRate) : ''
        });
        setEditingId(plan._id);
        setFormErrors({});
        setShowFormModal(true);
    };

    const handleAddNew = () => {
        setForm(getDefaultForm());
        setEditingId(null);
        setFormErrors({});
        setShowFormModal(true);
    };
    
    const handleDelete = (id) => {
        setShowDeleteModal(true);
        setDeleteId(id);
    };

    const confirmDelete = async () => {
        setSubmitting(true);
        try {
            await apiWithAuth.delete(`/subscription/admin/plan/${deleteId}`);
            await fetchPlans();
            setShowDeleteModal(false);
            setMessage("Plan deleted successfully!");
        } catch (err) {
             setError(err.response?.data?.message || err.message || "Error deleting plan");
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelEdit = () => {
        setShowFormModal(false);
    };

    const handleView = (plan) => {
        setViewPlan(plan);
    };
    
    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => setMessage(""), 3000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    return (
        <div className="bg-gray-100 min-h-screen p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Subscription Plans</h1>
                        <p className="mt-1 text-gray-600">Manage all subscription plans for your platform.</p>
                    </div>
                    <button onClick={handleAddNew} className="mt-4 sm:mt-0 flex items-center justify-center px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors">
                        <PlusIcon /> Add New Plan
                    </button>
                </header>
                
                {message && (
                     <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6" role="alert">
                        <p>{message}</p>
                    </div>
                )}
                {error && <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert"><p>{error}</p></div>}
                
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <PlanTable plans={plans} loading={loading} submitting={submitting} onEdit={handleEdit} onDelete={handleDelete} onView={handleView} />
                </div>
            </div>
            
            {showFormModal && (
                <div className="fixed inset-0 bg-black bg-opacity-60 z-40 flex justify-center items-center p-4">
                    <PlanForm form={form} formErrors={formErrors} onChange={handleInputChange} onSubmit={handleSubmit} onCancel={handleCancelEdit} editingId={editingId} submitting={submitting} />
                </div>
            )}
            
            <PlanViewModal open={!!viewPlan} plan={viewPlan} onClose={() => setViewPlan(null)} />
            <DeleteModal open={showDeleteModal} onCancel={() => setShowDeleteModal(false)} onConfirm={confirmDelete} submitting={submitting} />
        </div>
    );
}